/** GENERATED_ASSET — resolved generation context shared by every painter. */
import type { DetailTier, FountainParams, FountainRecipe, FountainVariant, ResolvedPalette, StyleProfile } from './types';
import { DETAIL, STYLE, deriveSwatches, mix } from './palette';
import type { Swatches } from './palette';
import { buildLayout } from './layout';
import type { Layout } from './layout';
import { Rng } from './rng';

/** Variant personality beyond the four numeric controls. */
export interface VariantCfg {
  /** 0..1 pull of stone swatches toward moss green. */
  greenTint: number;
  brassTarnish: number;
  /** Wet/tide stain opacity on stone. */
  stain: number;
  hangingMoss: boolean;
  brokenRim: boolean;
  brokenStele: boolean;
  crackBias: number;
  saltBias: number;
  /** Extra moss weight on lower/wet surfaces. */
  mossBias: number;
}

export const VARIANTS: Readonly<Record<FountainVariant, VariantCfg>> = {
  harbor: { greenTint: 0, brassTarnish: 0.08, stain: 0.12, hangingMoss: false, brokenRim: false, brokenStele: false, crackBias: 0.8, saltBias: 1.1, mossBias: 1 },
  mossgrown: { greenTint: 0.16, brassTarnish: 0.45, stain: 0.2, hangingMoss: true, brokenRim: false, brokenStele: false, crackBias: 0.9, saltBias: 0.6, mossBias: 1.3 },
  dry_ruin: { greenTint: 0.05, brassTarnish: 0.9, stain: 0.3, hangingMoss: false, brokenRim: true, brokenStele: true, crackBias: 1.5, saltBias: 1.4, mossBias: 0.9 },
};

export interface Ctx {
  recipe: FountainRecipe;
  p: FountainParams;
  palette: ResolvedPalette;
  sw: Swatches;
  style: StyleProfile;
  detail: { tier: DetailTier; grainMarks: number; minorRepairs: boolean };
  L: Layout;
  v: VariantCfg;
  seed: number;
  ns: string;
  /** Optional 0..1 water-motion phase; undefined = static. Never alters geometry/anchors. */
  phase: number | undefined;
  rng: (channel: string) => Rng;
}

export function makeCtx(recipe: FountainRecipe, palette: ResolvedPalette, ns: string, phase?: number): Ctx {
  const p = recipe.params;
  const v = VARIANTS[p.variant];
  const base = deriveSwatches(palette);
  const sw: Swatches = v.greenTint === 0 ? base : {
    ...base,
    stone: {
      ...base.stone,
      base: mix(base.stone.base, palette.moss, v.greenTint), light: mix(base.stone.light, palette.moss, v.greenTint * 0.6),
      shadow: mix(base.stone.shadow, palette.reedShadow, v.greenTint),
    },
  };
  const d = DETAIL[recipe.detail];
  return {
    recipe, p, palette, sw, style: STYLE, detail: { tier: recipe.detail, ...d },
    L: buildLayout(p, recipe.seed, p.variant), v, seed: recipe.seed, ns, phase,
    rng: (channel) => new Rng(recipe.seed, channel),
  };
}
